<template>
  <div class="cookie-consent" v-if="needsConsent || showSettings">
    <!-- แถบขอความยินยอมด้านล่าง -->
    <transition name="cc-slide">
      <div
        class="cc-banner"
        v-if="needsConsent && !showSettings"
        role="region"
        aria-label="การยินยอมใช้คุกกี้"
      >
        <div class="cc-banner__icon">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              d="M21.95 10.99a1 1 0 0 0-.95-.74 2.5 2.5 0 0 1-2.4-3.2 1 1 0 0 0-1.16-1.25 2.5 2.5 0 0 1-2.99-2.99 1 1 0 0 0-1.27-1.16A10 10 0 1 0 22 12c0-.34-.02-.68-.05-1.01zM8 16a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm.5-6a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm5 6.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm2.5-4a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"
            />
          </svg>
        </div>
        <div class="cc-banner__text">
          <p class="cc-banner__title">เว็บไซต์นี้ใช้คุกกี้</p>
          <p class="cc-banner__desc">
            เราใช้คุกกี้เพื่อมอบประสบการณ์การใช้งานที่ดีที่สุดแก่คุณ
            คุณสามารถเลือกยอมรับ ปฏิเสธ หรือจัดการการตั้งค่าได้ตามต้องการ
            <a href="/cookie-policy" class="cc-link">นโยบายคุกกี้</a>
          </p>
        </div>
        <div class="cc-banner__actions">
          <button
            type="button"
            class="cc-btn cc-btn--link"
            @click="openSettings"
          >
            ตั้งค่าคุกกี้
          </button>
          <button
            type="button"
            class="cc-btn cc-btn--outline"
            @click="rejectAll"
          >
            ปฏิเสธ
          </button>
          <button type="button" class="cc-btn cc-btn--fill" @click="acceptAll">
            ยอมรับทั้งหมด
          </button>
        </div>
      </div>
    </transition>

    <!-- หน้าต่างตั้งค่ากลางจอ -->
    <transition name="cc-fade">
      <div class="cc-overlay" v-if="showSettings" @click.self="closeSettings">
        <div
          class="cc-modal"
          role="dialog"
          aria-modal="true"
          aria-label="ตั้งค่าความเป็นส่วนตัว"
        >
          <div class="cc-modal__header">
            <span class="cc-modal__heading">
              <b-icon icon="sliders" aria-hidden="true"></b-icon>
              ตั้งค่าความเป็นส่วนตัว
            </span>
            <button
              type="button"
              class="cc-modal__close"
              aria-label="ปิด"
              @click="closeSettings"
            >
              <b-icon icon="x" aria-hidden="true"></b-icon>
            </button>
          </div>

          <div class="cc-modal__body">
            <div class="cc-row" v-for="cat in catMeta" :key="cat.key">
              <b-icon
                :icon="cat.icon"
                class="cc-row__icon"
                aria-hidden="true"
              ></b-icon>
              <div class="cc-row__text">
                <p class="cc-row__title">{{ cat.title }}</p>
                <p class="cc-row__desc">{{ cat.desc }}</p>
              </div>
              <span v-if="cat.locked" class="cc-badge">เปิดเสมอ</span>
              <label v-else class="cc-switch">
                <input type="checkbox" v-model="localCats[cat.key]" />
                <span class="cc-slider"></span>
              </label>
            </div>
          </div>

          <div class="cc-modal__footer">
            <button
              type="button"
              class="cc-btn cc-btn--outline"
              @click="savePreferences"
            >
              บันทึกการตั้งค่า
            </button>
            <button
              type="button"
              class="cc-btn cc-btn--fill"
              @click="acceptAll"
            >
              ยอมรับทั้งหมด
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "CookieConsent",
  data() {
    return {
      showSettings: false,
      // สำเนาค่าที่ผู้ใช้กำลังปรับในหน้าตั้งค่า (ยังไม่บันทึกจนกว่าจะกดปุ่ม)
      localCats: {
        necessary: true,
        analytics: false,
        marketing: false,
        functionality: false,
        targeting: false,
      },
      catMeta: [
        {
          key: "necessary",
          icon: "shield-lock",
          title: "คุกกี้ที่จำเป็น",
          desc: "จำเป็นต่อการทำงานพื้นฐานของเว็บไซต์ ไม่สามารถปิดการใช้งานได้",
          locked: true,
        },
        {
          key: "analytics",
          icon: "bar-chart",
          title: "คุกกี้เพื่อการวิเคราะห์",
          desc:
            "ช่วยให้เราเข้าใจว่าผู้ใช้มีปฏิสัมพันธ์กับเว็บไซต์อย่างไร เพื่อนำไปปรับปรุง",
        },
        {
          key: "marketing",
          icon: "megaphone",
          title: "คุกกี้เพื่อการตลาด",
          desc:
            "ติดตามกิจกรรมของคุณเพื่อนำเสนอเนื้อหาและโฆษณาที่เกี่ยวข้องมากขึ้น",
        },
        {
          key: "functionality",
          icon: "gear",
          title: "คุกกี้เพื่อการใช้งาน",
          desc:
            "จดจำการตั้งค่าและการเข้าชมของคุณ เพื่อมอบเนื้อหาที่เหมาะสมกับคุณ",
        },
        {
          key: "targeting",
          icon: "bullseye",
          title: "คุกกี้เพื่อกำหนดเป้าหมายและโฆษณา",
          desc: "ใช้เพื่อแสดงโฆษณาที่ตรงกับความสนใจของคุณบนเว็บไซต์อื่น",
        },
      ],
    };
  },
  computed: {
    ...mapGetters("Consent", ["needsConsent"]),
  },
  mounted() {
    // ให้ลิงก์ "ตั้งค่าคุกกี้" ที่ footer เปิดหน้าตั้งค่าได้ทุกเมื่อ
    this.$root.$on("open-cookie-settings", this.openSettings);
  },
  beforeDestroy() {
    this.$root.$off("open-cookie-settings", this.openSettings);
  },
  methods: {
    openSettings() {
      // เริ่มจากค่าที่ผู้ใช้เคยเลือกไว้ (หรือค่าเริ่มต้น)
      this.localCats = {
        ...this.localCats,
        ...this.$store.state.Consent.categories,
      };
      this.showSettings = true;
    },
    closeSettings() {
      this.showSettings = false;
    },
    acceptAll() {
      this.$store.dispatch("Consent/acceptAll");
      this.showSettings = false;
    },
    rejectAll() {
      this.$store.dispatch("Consent/rejectAll");
      this.showSettings = false;
    },
    savePreferences() {
      this.$store.dispatch("Consent/savePreferences", { ...this.localCats });
      this.showSettings = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "/src/assets/scss/variables.scss";

.cookie-consent {
  font-family: $font-family;
}

/* ---------- แถบด้านล่าง ---------- */
.cc-banner {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 16px;
  z-index: 3000;
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  background: #ffffff;
  border-top: 3px solid $primary-200;
  border-radius: 12px;
  box-shadow: $box-shadow-1;
  padding: 16px 20px;
}

.cc-banner__icon {
  color: $primary-200;
  flex: none;
}

.cc-banner__text {
  flex: 1;
  min-width: 220px;
}

.cc-banner__title {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 500;
  color: $text-black;
}

.cc-banner__desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #5b6566;
}

.cc-banner__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.cc-link {
  color: $nav-bar-color;
  text-decoration: underline;
}

/* ---------- ปุ่ม ---------- */
.cc-btn {
  font-family: $font-family;
  font-weight: 300;
  font-size: 14px;
  cursor: pointer;
  border-radius: 8px;
  padding: 9px 18px;
  transition: background 0.15s, color 0.15s;
  border: 1px solid transparent;
  white-space: nowrap;
}

.cc-btn--fill {
  background: $primary-200;
  color: #ffffff;
  border-color: $primary-200;

  &:hover {
    background: $primary-300;
    border-color: $primary-300;
  }
}

.cc-btn--outline {
  background: #ffffff;
  color: $primary-200;
  border-color: $primary-200;

  &:hover {
    background: #eef4fb;
  }
}

.cc-btn--link {
  background: transparent;
  color: $nav-bar-color;
  border: none;
  text-decoration: underline;
  padding: 9px 10px;
}

/* ---------- หน้าต่างตั้งค่า ---------- */
.cc-overlay {
  position: fixed;
  inset: 0;
  z-index: 3100;
  background: rgba(0, 38, 78, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.cc-modal {
  width: 100%;
  max-width: 460px;
  max-height: 90vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
}

.cc-modal__header {
  position: sticky;
  top: 0;
  background: $nav-bar-color;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.cc-modal__heading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
}

.cc-modal__close {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  font-size: 18px;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #ffffff;
  }
}

.cc-modal__body {
  padding: 6px 20px 4px;
}

.cc-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid #ececec;

  &:last-child {
    border-bottom: none;
  }
}

.cc-row__icon {
  flex: none;
  color: $primary-200;
  font-size: 20px;
  margin-top: 2px;
}

.cc-row__text {
  flex: 1;
}

.cc-row__title {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: $text-black;
}

.cc-row__desc {
  margin: 2px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: #5b6566;
}

.cc-badge {
  flex: none;
  align-self: center;
  background: #e6f1fb;
  color: #185fa5;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 8px;
  white-space: nowrap;
}

/* ---------- สวิตช์เปิด/ปิด ---------- */
.cc-switch {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 24px;
  flex: none;
  align-self: center;
  margin: 0;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
}

.cc-slider {
  position: absolute;
  inset: 0;
  cursor: pointer;
  background: $bg-300;
  border-radius: 24px;
  transition: 0.2s;

  &::before {
    content: "";
    position: absolute;
    height: 18px;
    width: 18px;
    left: 3px;
    top: 3px;
    background: #ffffff;
    border-radius: 50%;
    transition: 0.2s;
  }
}

input:checked + .cc-slider {
  background: $primary-200;
}

input:checked + .cc-slider::before {
  transform: translateX(18px);
}

input:focus-visible + .cc-slider {
  box-shadow: 0 0 0 3px rgba(42, 109, 180, 0.3);
}

.cc-modal__footer {
  position: sticky;
  bottom: 0;
  background: #ffffff;
  display: flex;
  gap: 10px;
  padding: 14px 20px 18px;

  .cc-btn {
    flex: 1;
  }
}

/* ---------- ทรานซิชัน ---------- */
.cc-slide-enter-active,
.cc-slide-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.cc-slide-enter,
.cc-slide-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

.cc-fade-enter-active,
.cc-fade-leave-active {
  transition: opacity 0.2s ease;
}

.cc-fade-enter,
.cc-fade-leave-to {
  opacity: 0;
}

/* ---------- จอเล็ก ---------- */
@media (max-width: 600px) {
  .cc-banner__actions {
    width: 100%;

    .cc-btn {
      flex: 1;
    }

    .cc-btn--link {
      flex-basis: 100%;
      order: 3;
    }
  }
}
</style>
